import React, { forwardRef, memo, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form';
//components
import FormElement from '../Utility/FormElement';
import FieldImageUpload from '../Utility/FieldImageUpload';
import { ImageUploadZoneHandle } from '../Utility/ImageUploadZone';
import { NFPA_CLASSIFICATION_DATA_FIELD_ID, NORMAL_DAMPER_IMAGE_UPLOAD, ACTIVATED_DAMPER_IMAGE_UPLOAD, DAMPER_IMAGE_UPLOADS, getClearedDamperCategories } from '../Utility/ImageUploadFields';
import ConfirmationBox from 'components/common/Message/ConfirmationBox/ConfirmationBox';
import { IMAGE_UPLOAD_DATAFIELDS } from '../Utility/ImageUploadFields';
//redux
import { useAppSelector } from 'redux/hooks';
import { useDispatch } from 'react-redux';
import { getAdditionalDatafieldSets, getDatafieldValues, setAdditionalDatafieldSets, setAdditionalDatafieldValues } from 'redux/common/commonSlice';
import { setIsDevReadingChanged, setIsTestedDateChanged, getFieldAttachments } from 'redux/pages/Inspection/InspectionTask/InspectionDetails/Equipment/EquipmentSlice';
import moment from 'moment';

interface EquipmentFormPropType {
    datafields?: any;
    ddAppendTo?: 'self' | undefined;
    isEditForm?: boolean;
    onFormSubmit?: (body, reset, setValue, fieldImages?) => void;
    body?: any;
    uniqueInspectionTaskId?: any;
    equipmentId?: any;
    // Changes once images have been committed, to reload the attachment lists so the just-saved
    // picks come back as seeded (already-saved) images instead of looking unsaved.
    attachmentsRefreshKey?: any;
    // Raised while any upload zone is uploading or loading its saved images, so the owning modal can
    // disable its buttons until the zones have settled.
    onImagesBusyChange?: (isBusy: boolean) => void;
}
const EquipmentForm = forwardRef<any, EquipmentFormPropType>((props, ref) => {

    const { datafields, isEditForm, onFormSubmit = () => { }, body, ddAppendTo = undefined, uniqueInspectionTaskId, equipmentId, attachmentsRefreshKey, onImagesBusyChange = () => { } } = props;
    const { control, formState: { errors }, handleSubmit, watch, setValue, unregister, reset } = useForm({mode:'all'});
    const addForm = useRef<any>(null);
    const [datafieldlist, setDatafieldList] = useState<any>([]);
    const [focusElement, setFocusElement] = useState<any>(null);
    // Attached images, keyed by uniqueDataFieldId.
    const fieldImages = useRef<any>({});
    const uploadingFields = useRef<any>({});
    const [showDamperUpload, setShowDamperUpload] = useState<boolean>(false);
    const normalDamperRef = useRef<ImageUploadZoneHandle>(null);
    const activatedDamperRef = useRef<ImageUploadZoneHandle>(null);
    const nfpaValueRef = useRef<any>(undefined);
    const [pendingNfpaChange, setPendingNfpaChange] = useState<{ uniqueDataFieldId: any; previousValue: any; newValue: any; showDamperUpload: boolean } | null>(null);
    const [initialFieldAttachments, setInitialFieldAttachments] = useState<any>({});
    const [imagesSavedTick, setImagesSavedTick] = useState(0);
    const [discardImagesTick, setDiscardImagesTick] = useState(0);

    const dispatch = useDispatch<any>();
    const additionalFieldValues = useAppSelector((state) => state.common.additionalDatafieldValues);
    const additionalDatafieldSets = useAppSelector((state) => state.common.additionalDatafieldSets);
    const loggedInUserDetails = useAppSelector((state) => state.administration.loggedInUserDetails);

    const fetchFieldAttachments = useCallback(() => {
        if (!isEditForm || !uniqueInspectionTaskId || !equipmentId) return;
        setInitialFieldAttachments({ equipmentId });
        fieldImages.current = {};
        uploadingFields.current = {};
        let isCurrent = true;

        const toAttachments = (attachmentList: any[]) => (attachmentList ?? []).map((a: any) => ({
            attachmentId: a.attachmentId,
            fileName: a.fileName,
        }));
        Object.entries(IMAGE_UPLOAD_DATAFIELDS).forEach(([dataFieldId, config]) => {
            dispatch(getFieldAttachments(uniqueInspectionTaskId, equipmentId, dataFieldId, (attachmentList: any[]) => {
                if (!isCurrent) return;
                setInitialFieldAttachments((current: any) => ({ ...current, [dataFieldId]: toAttachments(attachmentList) }));
            }, config.fileCategoryId));
        });
        DAMPER_IMAGE_UPLOADS.forEach((config) => {
            dispatch(getFieldAttachments(uniqueInspectionTaskId, equipmentId, NFPA_CLASSIFICATION_DATA_FIELD_ID, (attachmentList: any[]) => {
                if (!isCurrent) return;
                setInitialFieldAttachments((current: any) => ({ ...current, [config.key]: toAttachments(attachmentList) }));
            }, config.fileCategoryId));
        });

        return () => { isCurrent = false; };
    }, [isEditForm, uniqueInspectionTaskId, equipmentId, attachmentsRefreshKey, dispatch]);

    useEffect(() => {
        fetchFieldAttachments();
    }, [fetchFieldAttachments]);

    useImperativeHandle(ref, () => ({
        requestSubmit: () => {
            addForm?.current?.requestSubmit();
        },
        getFieldImages: () => getAttachedFieldImages(),
        isUploadingImages: () => Object.values(uploadingFields.current).some(Boolean),
        // Only the user's own picks count. Deliberately does NOT treat "uploading" as unsaved work:
        // seeded images are re-processed through FilePond when a zone loads, so an upload in flight
        // is just as likely to be the form loading saved images - which made closing mid-load prompt
        // "the attached image(s) have not been saved" about images that were already saved.
        hasUnsavedFieldImages: () =>
            Object.values(fieldImages.current).some((item: any) => (item?.unsavedFileCount ?? 0) > 0),
        onFieldImagesSaved: () => {
            normalDamperRef.current?.reset();
            activatedDamperRef.current?.reset();
            fieldImages.current = {};
            setImagesSavedTick((tick) => tick + 1);
            fetchFieldAttachments();
        },
        // Called when the user confirms leaving without saving. Without this the picked images stayed
        // in their zones, so they followed the form to the next equipment and were still there on
        // coming back - even though they had been discarded.
        discardUnsavedFieldImages: () => {
            normalDamperRef.current?.reset();
            activatedDamperRef.current?.reset();
            fieldImages.current = {};
            uploadingFields.current = {};
            setDiscardImagesTick((tick) => tick + 1);
        },
    }));

    const getAttachedFieldImages = () =>
        Object.keys(fieldImages.current)
            .map((uniqueDataFieldId) => ({ uniqueDataFieldId, ...fieldImages.current[uniqueDataFieldId] }))
            .filter((item: any) => item?.tempStorageDirectory);

    const onFieldImagesChange = useCallback((uniqueDataFieldId, dataFieldId, tempStorageDirectory, fileCount, fileCategoryId?, unsavedFileCount?) => {
        if (fileCount) {
            fieldImages.current[uniqueDataFieldId] = { dataFieldId, tempStorageDirectory, fileCount, fileCategoryId, unsavedFileCount };
        } else {
            delete fieldImages.current[uniqueDataFieldId];
        }
    }, []);

    const onFieldImageUploadingChange = useCallback((uniqueDataFieldId, isUploading) => {
        uploadingFields.current[uniqueDataFieldId] = isUploading;
    }, []);

    // Any zone busy uploading or loading its saved images. Held in state, not a ref, because the
    // buttons that depend on it have to re-render when it changes.
    const [busyImageFields, setBusyImageFields] = useState<any>({});
    const onFieldImageBusyChange = useCallback((uniqueDataFieldId, isBusy) => {
        setBusyImageFields((current: any) => {
            if (!!current[uniqueDataFieldId] === !!isBusy) return current;
            return { ...current, [uniqueDataFieldId]: isBusy };
        });
    }, []);

    const onImagesBusyChangeRef = useRef(onImagesBusyChange);
    useEffect(() => {
        onImagesBusyChangeRef.current = onImagesBusyChange;
    });

    useEffect(() => {
        onImagesBusyChangeRef.current(Object.values(busyImageFields).some(Boolean));
    }, [busyImageFields]);

    const getSelectedNfpaOption = (fieldList) => {
        const nfpaField = fieldList?.find((item) => item?.dataFieldId === NFPA_CLASSIFICATION_DATA_FIELD_ID);
        return nfpaField?.validatedValues?.find((option) => option?.isSelected);
    };

    const onConfirmDamperClear = () => {
        // deleteAllImages, not reset: the prompt says the attached images will be removed, so the
        // saved ones must go from the server too or they reappear on the next Edit.
        normalDamperRef.current?.deleteAllImages();
        activatedDamperRef.current?.deleteAllImages();
        if (pendingNfpaChange) {
            nfpaValueRef.current = pendingNfpaChange.newValue;
        }
        setShowDamperUpload(!!pendingNfpaChange?.showDamperUpload);
        setPendingNfpaChange(null);
    };

    const onCancelDamperClear = () => {
        if (pendingNfpaChange) {
            setValue(pendingNfpaChange.uniqueDataFieldId, pendingNfpaChange.previousValue);
        }
        setPendingNfpaChange(null);
    };

    useEffect(() => {
        let fieldsConv = JSON.parse(JSON.stringify(datafields))
        if (fieldsConv?.length) {
            fieldsConv.forEach(element => {  
                const devReadingDateIndex = fieldsConv?.findIndex((item) => item.dataFieldName === "Dev Reading Date");
                if (devReadingDateIndex > 0) {
                    if (element?.dataFieldName === "Dev Reading") {
                        if (element?.fieldValue === '') {
                            fieldsConv[devReadingDateIndex].disabled = true;
                        }
                        else {
                            fieldsConv[devReadingDateIndex].disabled = false;
                        }
                    }
                }
                    
                if (element?.dataEntryControl?.dataEntryControlName === "Drop-down list") {
                    const testDateIndex = fieldsConv?.findIndex((item) => item.dataFieldName === "Test Date");
                    if (element?.dataFieldName=== "Test Result") {
                        const slectedItem = element.validatedValues?.filter((item) => item?.isSelected);
                        if (slectedItem[0]?.dataFieldLookupValue === "Not Tested"){                  
                        fieldsConv[testDateIndex].disabled = true;
                    }
                    else{
                        fieldsConv[testDateIndex].disabled = false;
                    }
                    }
                    if (element?.dataFieldName=== "Test Method") {
                        const slectedItem = element.validatedValues?.filter((item) => item?.isSelected);
                        if (slectedItem[0]?.dataFieldLookupValue === "None"){                  
                        fieldsConv[testDateIndex].disabled = true;
                    }
                    else{
                        fieldsConv[testDateIndex].disabled = false;
                    }
                    }
                    if (element.defaultSelectNeeded)
                        element.validatedValues.unshift({ dataFieldLookupId: -1, dataFieldLookupValue: "--Select--", isSelected: false });
                    const slectedItem = element.validatedValues?.filter((item) => item?.isSelected);
                    if (slectedItem?.length) {
                        setValue(element.uniqueDataFieldId, slectedItem[0].dataFieldLookupId);
                    } else {
                        setValue(element.uniqueDataFieldId, -1)
                    }
                } else if (element?.dataEntryControl?.dataEntryControlName === "Check Box(s)") {
                    if (element.fieldValue) {
                        setValue(element.uniqueDataFieldId, element.fieldValue)
                    } else {
                        let list: any = [];
                        for (let j = 0; j < element?.validatedValues?.length; j++) {
                            let sm = element.validatedValues[j].dataFieldLookupId;
                            let item = {
                                [sm]: false,
                            };
                            list.push(item);
                        }
                        setValue(element?.uniqueDataFieldId, list);
                    }
                } else
                    setValue(element.uniqueDataFieldId, element.fieldValue);
            });
            const ascDatafields = [...fieldsConv].sort((a, b) => a.positionNo - b.positionNo);
            setDatafieldList([...ascDatafields]);
            const selectedNfpaOption = getSelectedNfpaOption(ascDatafields);
            setShowDamperUpload(!!selectedNfpaOption?.isAttachment);
            nfpaValueRef.current = selectedNfpaOption?.dataFieldLookupId;
        }
    }, [datafields]);

    useEffect(() => {
        if (additionalFieldValues) {
            let lists = JSON.parse(JSON.stringify(datafieldlist))
            let fieldValues = JSON.parse(JSON.stringify(additionalFieldValues));
            const findIndex = lists?.findIndex((item) => item.uniqueDataFieldId === fieldValues[1]);
            if (findIndex >= 0) {
                if (lists[findIndex]?.defaultSelectNeeded) {
                    fieldValues[0].unshift({ dataFieldLookupId: -1, dataFieldLookupValue: "--Select--", isSelected: false });
                }
                lists[findIndex].validatedValues = fieldValues[0];
            }
            const filterData: any = fieldValues[0].filter(
                (item: any) =>
                    item.isSelected === true
            );
            if (findIndex >= 0 && filterData.length > 0) {
                lists[findIndex].fieldValue = filterData[0].dataFieldLookupId.toString();
                setValue(lists[findIndex].uniqueDataFieldId, parseInt(filterData[0].dataFieldLookupId))
            }
            // findIndex is -1 when the response belongs to a field the form no longer shows: picking a
            // second NFPA Classification before the first one's lookup returns replaces the child
            // fields, so the late response has nothing to match. lists[-1] is undefined, and reading
            // uniqueDataFieldId off it crashed the page.
            else if (findIndex >= 0) {
                setValue(lists[findIndex].uniqueDataFieldId, -1)
            }

            setDatafieldList([...lists]);
            dispatch(setAdditionalDatafieldValues(""));
        }
    }, [additionalFieldValues]);

    useEffect(() => {
        if (additionalDatafieldSets?.length) {
            let lists = JSON.parse(JSON.stringify(datafieldlist))
            let count = 0;
            for (let i = 0; i < datafieldlist?.length; i++) {
                if (datafieldlist[i].parentUniqueDataFieldId) {
                    unregister(datafieldlist[i]?.uniqueDataFieldId);
                    lists.splice(i - count, 1);
                    count++;
                }
            }
            let fieldCount = 0;
            let filteredArray = JSON.parse(JSON.stringify(additionalDatafieldSets));
            for (let i = 0; i < lists?.length; i++) {
                for (let j = 0; j < filteredArray?.length; j++) {
                    if (lists[i].uniqueDataFieldId === filteredArray[j].uniqueDataFieldId && filteredArray[j].hasToBeDeleted === 0) {
                        filteredArray.splice(j, 1);
                        fieldCount++;
                    }
                }
            }

            for (let i = 0; i < filteredArray?.length; i++) {
                if (filteredArray[i]?.hasToBeDeleted === 0)
                    lists.push(filteredArray[i]);
                else {
                    const removeIndex = lists.findIndex((item) => item.uniqueDataFieldId === filteredArray[i].uniqueDataFieldId);
                    if (removeIndex >= 0) {
                        unregister(lists[removeIndex]?.uniqueDataFieldId);
                        lists.splice(removeIndex, 1);
                    }
                }
            }
            const ascDatafields = [...lists].sort((a, b) => a.positionNo - b.positionNo);
            setDatafieldList([...ascDatafields]);
            dispatch(setAdditionalDatafieldSets(""));
        }

    }, [additionalDatafieldSets])

    useLayoutEffect(() => {
        if (datafieldlist?.length) {
            if (focusElement) {
                const element = document.getElementsByName(focusElement);
                element?.[0]?.focus();
                setFocusElement(null);
            }
        }
    }, [datafieldlist])

    const onSubmit = (data) => {
        let fields: any = [];
        for (const field of datafieldlist) {
            const { uniqueDataFieldId } = field;
            if (data.hasOwnProperty(uniqueDataFieldId)) {
                let item: any = {
                    dataFieldId: uniqueDataFieldId,
                    value: null,
                    multipleValue: null,
                };
    
                if (Array.isArray(data[uniqueDataFieldId])) {
                    let multipleValue: any = [];
                    for (let i = 0; i < data[uniqueDataFieldId].length; i++) {
                        for (const obj in data[uniqueDataFieldId][i]) {
                            if (data[uniqueDataFieldId][i][obj]) {
                                multipleValue.push(obj);
                            }
                        }
                    }
                    item.multipleValue = multipleValue;
                } else {
                    if (field.dataEntryControl.dataEntryControlName === "Drop-down list" && data[uniqueDataFieldId] === -1) {
                        data[uniqueDataFieldId] = "";
                    }
                    if ((field.dataType.dataTypeId === 7) && (field.dataFieldName === "Test Date" || field.dataFieldName === "Dev Reading Date") && data[uniqueDataFieldId]?.length > 0) {
                        let newDate = new Date(data[uniqueDataFieldId]);
                        const siteTimeOffset = loggedInUserDetails?.userDefaults?.siteOffset;
                        data[uniqueDataFieldId] = moment(new Date(newDate.getTime() - (siteTimeOffset * 60000))).format("YYYY-MM-DD HH:mm:ss");
                    }
                    item.value = data[uniqueDataFieldId]?.toString();
                }
                fields.push(item);
            }
        }
        onFormSubmit(fields, reset, setValue, getAttachedFieldImages());
        dispatch(setIsDevReadingChanged(''));
        dispatch(setIsTestedDateChanged(''));
    }
    
    const onSelectionChange = useCallback((data, id) => {
        if (data?.dataFieldRelation !== undefined) {
            onFieldRelationChange(data, id);
        }
        if (data?.dataFieldSet !== undefined) {
            onDataFieldSetChange(data, id);
        }
        if (data?.dataFieldId === NFPA_CLASSIFICATION_DATA_FIELD_ID) {
            const selectedClassification = data?.validatedValues?.find((option) => option?.dataFieldLookupId === id);
            const nextShowDamperUpload = !!selectedClassification?.isAttachment;
            const hasDamperImages = (normalDamperRef.current?.getFileCount() ?? 0) > 0 || (activatedDamperRef.current?.getFileCount() ?? 0) > 0;
            const clearsDamperImages = getClearedDamperCategories(
                { isAttachment: showDamperUpload, dataFieldLookupId: nfpaValueRef.current },
                selectedClassification
            ).length > 0;
            if (clearsDamperImages && hasDamperImages) {
                setPendingNfpaChange({ uniqueDataFieldId: data.uniqueDataFieldId, previousValue: nfpaValueRef.current, newValue: id, showDamperUpload: nextShowDamperUpload });
            } else {
                nfpaValueRef.current = id;
                setShowDamperUpload(nextShowDamperUpload);
            }
        }
        onCheckDropDownChanges(data, id);

    }, [datafieldlist, showDamperUpload]);

    const onTextBoxChange = useCallback((data,e) => {
        onCheckTextBoxChange(data,e);
    }, [datafieldlist]);

    const onCheckTextBoxChange = (data,e) => {
        if (data.dataFieldName === "Dev Reading") {
            const devReadingDate = datafieldlist?.filter((item) => item.dataFieldName === "Dev Reading Date");
            const devReadingDateindex = datafieldlist?.findIndex((item) => item.dataFieldName === "Dev Reading Date");
            if (devReadingDateindex > 0){
                if(e.currentTarget.value?.length>0){
                    datafieldlist[devReadingDateindex].disabled = false;
                    setValue(devReadingDate[0]?.uniqueDataFieldId, moment(new Date()).utcOffset((loggedInUserDetails?.userDefaults?.siteOffset)).format("YYYY-MM-DD HH:mm:ss"));
                }else{
                    datafieldlist[devReadingDateindex].disabled = true;
                    setValue(devReadingDate[0]?.uniqueDataFieldId,''); 
                } 
            }
        }   
    }
    const onDateTimeChanged = useCallback((data,e) => {
        onCheckDateTimeChanged(data,e);
    }, [datafieldlist]);

    const onCheckDateTimeChanged = useCallback((data,e) => {
        if (data.dataFieldName === "Dev Reading Date") {
            dispatch(setIsDevReadingChanged('D'));
        } 
        if(data.dataFieldName === "Test Date"){
            dispatch(setIsTestedDateChanged('T'));
        }
    }, []);

    const onCheckDropDownChanges = (data, id) => {
        let isChecked = false;
        const testResIndex = datafieldlist?.findIndex((item) => item.dataFieldName === "Test Result");
        const testDateIndex = datafieldlist?.findIndex((item) => item.dataFieldName === "Test Date");
        if (data.dataFieldName === "Test Method") {
            if (id) {
                if (testResIndex >= 0) {
                    const testDate = datafieldlist?.filter((item) => item.dataFieldName === "Test Date");
                    const items = data?.validatedValues?.filter((item) => item.dataFieldLookupId === id);
                    if (items[0]?.dataFieldLookupValue !== "None") {
                        setValue(testDate[0]?.uniqueDataFieldId, moment(new Date()).utcOffset((loggedInUserDetails?.userDefaults?.siteOffset)).format("YYYY-MM-DD HH:mm:ss"));
                        datafieldlist[testDateIndex].disabled = false;
                    }
                    else {
                        setValue(testDate[0]?.uniqueDataFieldId,'');
                        datafieldlist[testDateIndex].disabled = true;
                    }
                    datafieldlist[testResIndex].disabled = false;
                    const none = datafieldlist[testResIndex]?.validatedValues?.filter((item) => item?.dataFieldLookupId !== 3);
                    datafieldlist[testResIndex].validatedValues = none;
                    const passed = datafieldlist[testResIndex]?.validatedValues?.filter((item) => item?.dataFieldLookupValue === "Passed");
                    if (passed?.length) setValue(datafieldlist[testResIndex]?.uniqueDataFieldId, passed[0].dataFieldLookupId);
                }
                if (testDateIndex >= 0) {
                    setValue(datafieldlist[testDateIndex]?.uniqueDataFieldId,  moment(new Date()).utcOffset((loggedInUserDetails?.userDefaults?.siteOffset)).format("YYYY-MM-DD HH:mm:ss"));
                }
            } else {
                if (testResIndex >= 0) {
                    const testDate = datafieldlist?.filter((item) => item.dataFieldName === "Test Date");
                    const items = data?.validatedValues?.filter((item) => item.dataFieldLookupId === id);
                    if (items[0]?.dataFieldLookupValue !== "None") {
                        setValue(testDate[0]?.uniqueDataFieldId, moment(new Date()).utcOffset((loggedInUserDetails?.userDefaults?.siteOffset)).format("YYYY-MM-DD HH:mm:ss"));
                        datafieldlist[testDateIndex].disabled = false;
                    }
                    else {
                        setValue(testDate[0]?.uniqueDataFieldId,'');
                        datafieldlist[testDateIndex].disabled = true;
                    }
                    datafieldlist[testResIndex].validatedValues.push({
                        dataFieldLookupId: 3,
                        dataFieldLookupValue: "Not Tested",
                        isSelected: true
                    })
                    setValue(datafieldlist[testResIndex]?.uniqueDataFieldId, 3);
                    datafieldlist[testResIndex].disabled = true;
                }
                if (testDateIndex >= 0) {
                    setValue(datafieldlist[testDateIndex]?.uniqueDataFieldId, "");
                }
            }
            isChecked = true;
        } else if (data.dataFieldName === "Test Result") {
            if (id) {
                const testDate = datafieldlist?.filter((item) => item.dataFieldName === "Test Date");
                const items = data?.validatedValues?.filter((item) => item.dataFieldLookupId === id);
                if (items[0]?.dataFieldLookupValue !== "Not Tested") {
                    setValue(testDate[0]?.uniqueDataFieldId, moment(new Date()).utcOffset((loggedInUserDetails?.userDefaults?.siteOffset)).format("YYYY-MM-DD HH:mm:ss"));
                    datafieldlist[testDateIndex].disabled = false;
                }
                else {
                    setValue(testDate[0]?.uniqueDataFieldId,'');
                    datafieldlist[testDateIndex].disabled = true;
                }   
            }
            isChecked = true;      
        }
        if (isChecked) {
            setFocusElement(data?.uniqueDataFieldId);
            setDatafieldList([...datafieldlist]);
        }

    }

    const onFieldRelationChange = (data, id) => {
        let service: any = data?.dataFieldRelation?.subSetValueService;
        if (service !== undefined) {
            service = service.replace("{dataFieldLookupId}", id);
            dispatch(getDatafieldValues(service, data?.dataFieldRelation?.childDataFieldId, body));
            setFocusElement(data?.uniqueDataFieldId);
        }
    }

    const onDataFieldSetChange = (data, id) => {
        let service: any = data?.dataFieldSet?.subSetValueService;
        if (service !== undefined) {
            service = service.replace("{dataFieldLookupId}", id);
            dispatch(getAdditionalDatafieldSets(service, body));
            setFocusElement(data?.uniqueDataFieldId);
        }
    }

    // Both damper zones belong to the NFPA Classification field, so they are rendered inline
    // right beneath it (see the map below) instead of at the end of the form.
    const damperUploadZones = (
        <>
            <div className="image-upload-host">
                <FieldImageUpload
                    ref={normalDamperRef}
                    config={NORMAL_DAMPER_IMAGE_UPLOAD}
                    uniqueDataFieldId={NORMAL_DAMPER_IMAGE_UPLOAD.key}
                    visible={showDamperUpload}
                    initialAttachments={initialFieldAttachments?.[NORMAL_DAMPER_IMAGE_UPLOAD.key]}
                    attachmentsEquipmentId={initialFieldAttachments?.equipmentId}
                    attachmentsRefreshKey={attachmentsRefreshKey}
                    uniqueInspectionTaskId={uniqueInspectionTaskId}
                    equipmentId={equipmentId}
                    onFilesChange={(tempStorageDirectory, fileCount, unsavedFileCount) =>
                        onFieldImagesChange(NORMAL_DAMPER_IMAGE_UPLOAD.key, NFPA_CLASSIFICATION_DATA_FIELD_ID, tempStorageDirectory, fileCount, NORMAL_DAMPER_IMAGE_UPLOAD.fileCategoryId, unsavedFileCount)
                    }
                    onUploadingChange={(isUploading) => onFieldImageUploadingChange(NORMAL_DAMPER_IMAGE_UPLOAD.key, isUploading)}
                    onBusyChange={(isBusy) => onFieldImageBusyChange(NORMAL_DAMPER_IMAGE_UPLOAD.key, isBusy)}
                />
            </div>
            <div className="image-upload-host">
                <FieldImageUpload
                    ref={activatedDamperRef}
                    config={ACTIVATED_DAMPER_IMAGE_UPLOAD}
                    uniqueDataFieldId={ACTIVATED_DAMPER_IMAGE_UPLOAD.key}
                    visible={showDamperUpload}
                    initialAttachments={initialFieldAttachments?.[ACTIVATED_DAMPER_IMAGE_UPLOAD.key]}
                    attachmentsEquipmentId={initialFieldAttachments?.equipmentId}
                    attachmentsRefreshKey={attachmentsRefreshKey}
                    uniqueInspectionTaskId={uniqueInspectionTaskId}
                    equipmentId={equipmentId}
                    onFilesChange={(tempStorageDirectory, fileCount, unsavedFileCount) =>
                        onFieldImagesChange(ACTIVATED_DAMPER_IMAGE_UPLOAD.key, NFPA_CLASSIFICATION_DATA_FIELD_ID, tempStorageDirectory, fileCount, ACTIVATED_DAMPER_IMAGE_UPLOAD.fileCategoryId, unsavedFileCount)
                    }
                    onUploadingChange={(isUploading) => onFieldImageUploadingChange(ACTIVATED_DAMPER_IMAGE_UPLOAD.key, isUploading)}
                    onBusyChange={(isBusy) => onFieldImageBusyChange(ACTIVATED_DAMPER_IMAGE_UPLOAD.key, isBusy)}
                />
            </div>
        </>
    );

    return (
        <>
            <form className="form-data-wrapper" onSubmit={handleSubmit(onSubmit)} ref={addForm}>
                {datafieldlist?.length > 0 &&
                    datafieldlist?.map((item: any, key: any) => (
                        <React.Fragment key={item?.uniqueDataFieldId ?? key}>
                            <FormElement
                                data={item}
                                control={control}
                                ddAppendTo={ddAppendTo}
                                setValue={setValue}
                                watch={watch}
                                onSelectionChange={onSelectionChange}
                                onDateTimeChanged={onDateTimeChanged}
                                onTextBoxChange={onTextBoxChange}
                                onFieldImagesChange={onFieldImagesChange}
                                onFieldImageUploadingChange={onFieldImageUploadingChange}
                                onFieldImageBusyChange={onFieldImageBusyChange}
                                initialFieldAttachments={initialFieldAttachments}
                                attachmentsRefreshKey={attachmentsRefreshKey}
                                imagesSavedTick={imagesSavedTick}
                                discardImagesTick={discardImagesTick}
                                uniqueInspectionTaskId={uniqueInspectionTaskId}
                                equipmentId={equipmentId}
                                errors={errors}
                                 />
                            {item?.dataFieldId === NFPA_CLASSIFICATION_DATA_FIELD_ID && damperUploadZones}
                        </React.Fragment>
                    ))
                }
            </form>
            <ConfirmationBox
                showConfirmModal={!!pendingNfpaChange}
                setShowConfirmModal={() => setPendingNfpaChange(null)}
                confirmAction={onConfirmDamperClear}
                cancelAction={onCancelDamperClear}
                onHideAction={onCancelDamperClear}
                message="NFPA Classification has attached Damper image(s). Changing this will delete the image(s). Do you want to continue?"
            />
        </>
    )
})

export default memo(EquipmentForm)